import React, { useEffect, useRef, useContext, useState, useCallback } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaTimes,
  FaInfoCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import Button from "../Button/Button";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";

// ... Contexto e interfaces iguais

// Adicione a definição ou importação do contexto ToastMessagesContext
// Exemplo de definição mínima (ajuste conforme necessário):
import { createContext } from "react";
export const ToastMessagesContext = createContext({
  getDefaultSuccessTitle: () => "Successo",
  getDefaultErrorTitle: () => "Erro",
  getDefaultCloseLabel: () => "Fechar",
});

export interface ToastProps {
  visible: boolean;
  onClose?: () => void;
  type?: "success" | "error" | "info" | "warning";
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  duration?: number;
  theme?: "light" | "dark";
  className?: string;
  children?: React.ReactNode;
  "data-testid"?: string;
}

const Toast: React.FC<ToastProps> = ({
  visible,
  onClose,
  type = "info",
  title,
  description,
  icon,
  action,
  duration = 3000,
  theme = "light",
  className,
  children,
  "data-testid": dataTestId,
}) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const toastRef = useRef<HTMLDivElement>(null);
  const i18n = useContext(ToastMessagesContext);

  // Barra de progresso controlada por estado para pausar a animação
  const [progress, setProgress] = useState(100);
  const [paused, setPaused] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [remaining, setRemaining] = useState<number>(duration || 0);

  // Timer para auto-close e progresso visual
  useEffect(() => {
    if (!visible || typeof duration !== "number" || !onClose) {
      return;
    }

    setProgress(100);
    setRemaining(duration);
    setStartTime(Date.now());
    setPaused(false);

    const tick = () => {
      if (paused) return;
      const elapsed = Date.now() - (startTime || 0);
      const percent = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(percent);
      setRemaining(Math.max(duration - elapsed, 0));
      if (elapsed >= duration) onClose();
      else timerRef.current = setTimeout(tick, 100);
    };
    timerRef.current = setTimeout(tick, 100);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line
  }, [visible, duration, onClose, paused]);

  // Pausar/retomar timeout e barra de progresso
  const handleMouseEnter = useCallback(() => setPaused(true), []);
  const handleMouseLeave = useCallback(() => {
    setStartTime(Date.now() - (duration - remaining));
    setPaused(false);
    // eslint-disable-next-line
  }, [duration, remaining]);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  // ESC para fechar e focar toast ao abrir
  useEffect(() => {
    if (!visible || !onClose) return;
    toastRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose, handleClose]);

  // Ícone padrão por tipo
  const iconClasses = "text-2xl flex-shrink-0 opacity-90";
  const defaultIcon = type === "success"
    ? <FaCheckCircle className={iconClasses + " text-green-500"} aria-hidden="true" />
    : type === "error"
    ? <FaExclamationCircle className={iconClasses + " text-red-500"} aria-hidden="true" />
    : type === "warning"
    ? <FaExclamationTriangle className={iconClasses + " text-yellow-500"} aria-hidden="true" />
    : <FaInfoCircle className={iconClasses + " text-blue-500"} aria-hidden="true" />;

  // Título padrão por tipo
  const resolvedTitle = title ||
    (type === "success"
      ? i18n.getDefaultSuccessTitle?.()
      : type === "error"
      ? i18n.getDefaultErrorTitle?.()
      : undefined);

  // Cores e estilos por tipo e tema
  const typeStyles: Record<
    NonNullable<ToastProps["type"]>,
    { border: string; bg: string; bar: string }
  > = {
    success: {
      border: "border-l-4 border-green-500",
      bg: theme === "dark" ? "bg-green-950/90" : "bg-green-50",
      bar: "bg-gradient-to-r from-blue-500 to-green-400",
    },
    error: {
      border: "border-l-4 border-red-500",
      bg: theme === "dark" ? "bg-red-950/90" : "bg-red-50",
      bar: "bg-gradient-to-r from-red-500 to-pink-400",
    },
    info: {
      border: "border-l-4 border-blue-500",
      bg: theme === "dark" ? "bg-blue-950/90" : "bg-blue-50",
      bar: "bg-gradient-to-r from-blue-500 to-cyan-400",
    },
    warning: {
      border: "border-l-4 border-yellow-500",
      bg: theme === "dark" ? "bg-yellow-950/90" : "bg-yellow-50",
      bar: "bg-gradient-to-r from-yellow-500 to-orange-400",
    },
  };

  const baseClasses =
    "flex items-center gap-3 w-full max-w-md rounded-xl shadow-lg ring-1 ring-black/10 px-4 py-3 outline-none pointer-events-auto transition-all duration-400";
  const themeClasses =
    theme === "dark"
      ? "text-white bg-neutral-900"
      : "text-neutral-900 bg-white";
  const responsiveClasses =
    "min-w-0 sm:min-w-[320px] sm:max-w-[96vw] text-base";

  // Variants para animação do toast
  const toastVariants = {
    hidden: { opacity: 0, y: 32, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, type: "spring" as const } },
    exit: { opacity: 0, y: 32, scale: 0.95, transition: { duration: 0.3 } },
  };

  return (
    <div className="fixed inset-x-0 bottom-8 flex flex-col items-center z-[1000] pointer-events-none space-y-2 px-2 sm:px-4" role="presentation">
      <AnimatePresence>
        {visible && (
          <motion.div
            ref={toastRef}
            tabIndex={0}
            className={clsx(
              baseClasses,
              themeClasses,
              typeStyles[type].border,
              typeStyles[type].bg,
              responsiveClasses,
              className,
              "relative focus:ring-2 focus:ring-blue-500"
            )}
            role={type === "error" ? "alert" : "status"}
            aria-live={type === "error" ? "assertive" : "polite"}
            aria-atomic="true"
            aria-label={
              resolvedTitle
                ? description
                  ? `${resolvedTitle}: ${description}`
                  : resolvedTitle
                : description
            }
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-testid={dataTestId}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={toastVariants}
          >
            <span className="mr-2">{icon ?? defaultIcon}</span>
            <div className="flex-1 min-w-0 flex flex-col">
              {resolvedTitle && (
                <div className="font-semibold text-[17px] leading-tight mb-0.5">{resolvedTitle}</div>
              )}
              {description && (
                <div className="text-sm opacity-85 leading-snug">{description}</div>
              )}
              {children}
            </div>
            {action && <div className="ml-2 flex items-center">{action}</div>}
            {onClose && (
              <Button
                onClick={handleClose}
                icon={<FaTimes />}
                ariaLabel={i18n.getDefaultCloseLabel?.() || "Fechar notificação"}
                theme={theme}
                size="tiny"
                className={`
                  ml-3 p-1.5 rounded-full hover:bg-neutral-200/70 focus:bg-blue-100 focus:outline-blue-500
                  ${theme === "dark" ? "hover:bg-neutral-800/70 focus:bg-blue-900" : ""}
                  text-xl text-neutral-500 hover:text-neutral-900 focus:text-blue-700
                  transition-colors
                `}
                buttonClassName=""
                tabIndex={0}
                title={i18n.getDefaultCloseLabel?.() || "Fechar notificação"}
                data-testid="toast-close"
              />
            )}
            {/* Barra de progresso controlada */}
            {typeof duration === "number" && duration > 0 && (
              <span
                className={`
                  absolute left-0 bottom-0 h-0.5 w-full rounded-b-xl
                  ${typeStyles[type].bar}
                  pointer-events-none
                  transition-none
                `}
                style={{
                  width: `${progress}%`,
                  transition: paused ? "none" : "width 0.1s linear",
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Toast;